/**
 * 체험용 게스트 계정.
 *
 * 처음 방문한 사람이 가입 절차 없이 바로 월드에 들어갈 수 있도록, 브라우저에서
 * `guest_xxxxxx` 계정과 임의의 비밀번호를 만들어 자동 로그인한다. 서버는
 * `AutoCreateAccounts=true`라 존재하지 않는 계정으로 처음 로그인하면 그 자리에서
 * 계정을 만들어 준다(`C_AuthLogin` → `L1Account.create`).
 *
 * 만들어진 계정은 서버의 `GuestAccountTimeController`가 마지막 접속으로부터
 * 7일 뒤에 캐릭터와 함께 지운다. 접두사 `guest_`는 그 컨트롤러와 맞춰야 한다.
 *
 * ## 이름 길이가 6자로 고정된 이유
 *
 * 캐릭터 이름은 계정 이름과 같게 만드는데, 서버 `C_CreateChar.isInvalidName()`이
 * 영숫자가 아닌 문자(여기서는 `_`)를 포함한 이름에 **12바이트** 제한을 건다.
 * `guest_`(6바이트) + 6자 = 정확히 12바이트라 이보다 길어질 수 없다.
 */

import { storageManager } from '../core/StorageManager';
import { isLoginRoute } from './route';

/** 게스트 계정 접두사. 서버 GuestAccountTimeController.GUEST_PREFIX와 같아야 한다. */
export const GUEST_PREFIX = 'guest_';

/** 접두사 뒤에 붙는 임의 코드의 길이. 위 주석의 12바이트 제한에서 나온 값이다. */
const GUEST_CODE_LENGTH = 6;

/** 임의 코드에 쓰는 문자. 서버가 이름을 영숫자로 보도록 소문자+숫자만 쓴다. */
const CODE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

/** 비밀번호에 쓰는 문자. 서버로만 오가므로 사람이 읽을 필요는 없다. */
const PASSWORD_ALPHABET =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

const PASSWORD_LENGTH = 16;

const GUEST_KEY = 'l1j_guest';

export interface GuestCredentials {
  account: string;
  password: string;
  /** 만든 시각 (표시·디버깅용). 만료 판정의 진실은 서버의 last_activated_at이다. */
  createdAt: number;
}

/**
 * 암호학적 난수로 문자열을 만든다.
 *
 * `Math.random()`을 쓰지 않는 이유: 게스트 계정의 이름이 곧 다른 사람과 겹치면
 * 안 되는 식별자이고, 비밀번호는 그 계정의 유일한 자물쇠이기 때문이다.
 */
function randomString(length: number, alphabet: string): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
}

/** 주어진 이름이 게스트 계정인지. */
export function isGuestAccount(account: string | null | undefined): boolean {
  return !!account && account.startsWith(GUEST_PREFIX);
}

/** 새 게스트 자격증명을 만든다 (저장은 하지 않는다). */
export function createGuestCredentials(): GuestCredentials {
  return {
    account: GUEST_PREFIX + randomString(GUEST_CODE_LENGTH, CODE_ALPHABET),
    password: randomString(PASSWORD_LENGTH, PASSWORD_ALPHABET),
    createdAt: Date.now(),
  };
}

/**
 * 저장된 게스트 자격증명을 읽는다.
 *
 * StorageManager의 `l1j_credentials`는 보안상 비밀번호를 저장하지 않으므로,
 * 자동 로그인에 비밀번호가 필요한 게스트는 별도 키에 따로 둔다. 사람이 정한
 * 비밀번호가 아니라 이 브라우저가 만들어 낸 임의 문자열이라 노출 위험이 다르다.
 */
export function loadGuestCredentials(): GuestCredentials | null {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GuestCredentials>;
    if (!parsed.account || !parsed.password) return null;
    if (!isGuestAccount(parsed.account)) return null;
    return {
      account: parsed.account,
      password: parsed.password,
      createdAt: parsed.createdAt ?? Date.now(),
    };
  } catch (e) {
    console.warn('[guest] 저장된 게스트 자격증명을 읽지 못했습니다:', e);
    return null;
  }
}

/** 게스트 자격증명을 저장한다. */
export function saveGuestCredentials(creds: GuestCredentials): void {
  try {
    localStorage.setItem(GUEST_KEY, JSON.stringify(creds));
  } catch (e) {
    console.warn('[guest] 게스트 자격증명을 저장하지 못했습니다:', e);
  }
}

/** 게스트 자격증명을 지운다 (로그아웃·삭제된 계정 정리용). */
export function clearGuestCredentials(): void {
  try {
    localStorage.removeItem(GUEST_KEY);
  } catch (e) {
    console.warn('[guest] 게스트 자격증명을 지우지 못했습니다:', e);
  }
}

/**
 * 이 브라우저가 쓸 게스트 자격증명을 가져온다. 없으면 만들어 저장한다.
 *
 * 두 번째 방문부터는 같은 계정으로 돌아오므로 캐릭터와 진행 상황이 유지된다
 * (서버가 7일 뒤에 지우기 전까지).
 */
export function ensureGuestCredentials(): GuestCredentials {
  const existing = loadGuestCredentials();
  if (existing) return existing;

  const created = createGuestCredentials();
  saveGuestCredentials(created);
  console.log('[guest] 새 게스트 계정을 만들었습니다:', created.account);
  return created;
}

/**
 * 게스트로 자동 로그인해야 하는 상황인지.
 *
 * `/login`으로 들어온 사람은 자기 계정으로 들어오려는 것이므로 가로채지 않는다.
 * 사람이 직접 만든 계정으로 로그인한 적이 있어도(= StorageManager에 자격증명이
 * 남아 있고 그것이 게스트가 아니면) 그 계정을 우선한다.
 */
export function shouldUseGuestLogin(): boolean {
  if (isLoginRoute()) return false;
  const stored = storageManager.loadCredentials();
  if (stored && !isGuestAccount(stored.account)) return false;
  return true;
}
